import React, { useState, useEffect, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import submitBooking from "../helper/submitBooking";
/** @typedef {import('../types/booking').BookingFieldMap} BookingFieldMap */
const languageOptions = {
  mr: { label: "Marathi", code: "mr-IN" },
  hi: { label: "Hindi", code: "hi-IN" },
  en: { label: "English", code: "en-IN" },
};

const questions = [
  {
    key: "name",
    question: {
      mr: "तुमचं नाव काय आहे?",
      hi: "आपका नाम क्या है?",
      en: "What is your name?",
    },
  },
  {
    key: "phone",
    question: {
      mr: "तुमचा फोन नंबर काय आहे?",
      hi: "आपका फ़ोन नंबर क्या है?",
      en: "What is your phone number?",
    },
  },
  {
    key: "email",
    question: {
      mr: "तुमचा ईमेल आयडी काय आहे?",
      hi: "आपका ईमेल क्या है?",
      en: "What is your email?",
    },
  },
  {
    key: "people",
    question: {
      mr: "एकूण किती लोक आहेत?",
      hi: "कुल कितने लोग हैं?",
      en: "How many people are coming?",
    },
  },
  {
    key: "checkin",
    question: {
      mr: "चेक-इन तारीख काय आहे?",
      hi: "चेक-इन की तारीख क्या है?",
      en: "What is the check-in date?",
    },
  },
  {
    key: "checkout",
    question: {
      mr: "चेक-आउट तारीख काय आहे?",
      hi: "चेक-आउट की तारीख क्या है?",
      en: "What is the check-out date?",
    },
  },
  {
    key: "roomType",
    question: {
      mr: "कशा प्रकारचं रूम पाहिजे?",
      hi: "किस प्रकार का कमरा चाहिए?",
      en: "What type of room do you want?",
    },
  },
  {
    key: "roomCount",
    question: {
      mr: "एकूण किती रूम्स हवेत?",
      hi: "कुल कितने कमरे चाहिए?",
      en: "How many rooms do you need?",
    },
  },
];

const VoiceChat = () => {
  const [selectedLang, setSelectedLang] = useState("mr");
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const recognitionRef = useRef(null);
  const isRecognizingRef = useRef(false);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [keyValueMap, setkeyValueMap] = useState({});
  const [started, setStarted] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const retryCountRef = useRef(0);
  const resultReceivedRef = useRef(false);
  const voluntaryStopRef = useRef(false);
  const maxRetries = 3;
  const functions = getFunctions(getApp(), "asia-south1");

  //for SpeechRecognition initialization on lang change
  useEffect(() => {
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = selectedLang === "mr" ? "mr-IN" : "en-US";
      recognitionRef.current.maxAlternatives = 1;
    } else {
      alert("⚠️ Your browser does not support speech recognition.");
    }
  }, [selectedLang]);

  //for handling reply from EVi
  useEffect(() => {
    // const utterance = new SpeechSynthesisUtterance(reply);
    // utterance.lang = languageOptions[selectedLang].code;

    // const voices = speechSynthesis.getVoices();
    // const sweetVoice = voices.find(
    //   (v) =>
    //     v.lang === utterance.lang && v.name.toLowerCase().includes("female")
    // );

    // utterance.voice =
    //   sweetVoice || voices.find((v) => v.lang === utterance.lang);
    // speechSynthesis.speak(utterance);

    // setCurrentQIndex((prev) => prev + 1);
    if (reply && !bookingComplete) {
      speak(reply, () => {
        console.log("reply speaking is completed");
        // ✅ Now update question index and go to next question
        setCurrentQIndex((prev) => prev + 1);
      });
    } else if (bookingComplete) {
      speak("Thank you! Your booking is complete.", () => {
        stopListening();
        // 🔁 your final submission
        submitBooking({
          formData: {
            fullName: keyValueMap.name,
            phone: keyValueMap.phone,
            email: keyValueMap.email,
            people: parseInt(keyValueMap.people),
          },
          checkinDate: parseDate(keyValueMap.checkin),
          checkoutDate: parseDate(keyValueMap.checkout),
          roomCount: parseInt(keyValueMap.roomCount),
          maxRoomLimit: 5, // or set dynamically
          selectedRoomType: keyValueMap.roomType,
          bootstrapBundleMin: window.bootstrap, // if using Bootstrap
        });
      });
    }
  }, [reply, selectedLang]);
  const parseDate = (str) => {
    const [day, month, year] = str.split("/").map(Number);
    return new Date(year, month - 1, day);
  };
  //start speaking from here
  useEffect(() => {
    if (!started || bookingComplete || isRecognizingRef.current) return;

    const currentQuestion = questions[currentQIndex];

    if (!currentQuestion) {
      speak("Thank you! Your booking is complete.", () => {
        console.log("complete booking info :- ", JSON.stringify(keyValueMap));
        setBookingComplete(true);
        stopListening();

        // 🔁 your final submission
        submitBooking({
          formData: {
            fullName: keyValueMap.name,
            phone: keyValueMap.phone,
            email: keyValueMap.email,
            people: parseInt(keyValueMap.people),
          },
          checkinDate: parseDate(keyValueMap.checkin),
          checkoutDate: parseDate(keyValueMap.checkout),
          roomCount: parseInt(keyValueMap.roomCount),
          maxRoomLimit: 5, // or set dynamically
          selectedRoomType: keyValueMap.roomType,
          bootstrapBundleMin: window.bootstrap, // if using Bootstrap
        });
      });

      return;
    }
    speak(currentQuestion.question[selectedLang], () => {
      console.log("Speaking done, now listening...");
      startListening();
    });
  }, [started, currentQIndex, bookingComplete]);

  const handleSendMessage = async (message) => {
    try {
      const backendService = httpsCallable(functions, "chatService");
      const response = await backendService({
        message,
        key: questions[currentQIndex].key,
        lang: selectedLang,
      });

      try {
        const res = JSON.parse(response.data.message);
        console.log("Message from Evi: ", res.message);

        const responseData = response.data.jsonAnsForKey;
        /** @type {BookingFieldMap} */
        const jsonAnsForKey = responseData;

        const key = questions[currentQIndex].key;
        const value = jsonAnsForKey[key];

        console.log("key : " + key + " & value :  " + value);

        if (!validateBookingData(key, value)) {
          const reprompt = `Hmm, the ${key} you gave doesn't seem valid.
        ${questions[currentQIndex].question[selectedLang]}`;

          speak(reprompt, () => startListening());
          return;
        }

        setReply(res.message);
        setkeyValueMap((prev) => ({
          ...prev,
          ...jsonAnsForKey,
        }));
      } catch (e) {
        console.error("Not valid JSON : ", e);
      }
    } catch (error) {
      console.error("❌ Backend API Error:", error);
      setReply("❌ Evi can't reply now. Please try again later.");
    }
  };

  const speak = async (text, onEndCallback) => {
    try {
      console.log("going to speak!!!");
      const googleTTS = httpsCallable(functions, "ttsService");
      const response = await googleTTS({
        text,
        langCode: selectedLang + "-IN",
      });

      const audioURL = response.data.audioURL;
      console.log("got the audio " + audioURL);
      if (response.data.cached) {
        console.log("cache is " + response.data.cached);
      }

      const audio = new Audio(audioURL);
      audio.play();
      audio.onended = () => {
        if (onEndCallback) onEndCallback();
      };
    } catch (error) {
      console.error("TTS Error:", error);
      alert("Backend Server Error. !!! server is down !!!");
      return;
      //  if (onEndCallback) onEndCallback();
    }
  };

  const stopListening = () => {
    voluntaryStopRef.current = true;
    if (recognitionRef.current && isRecognizingRef.current) {
      recognitionRef.current.stop();
      isRecognizingRef.current = false;
      setListening(false);
    }
  };

  const listenForConfirmation = (transcriptToConfirm) => {
    if (recognitionRef.current) {
      recognitionRef.current.onresult = (event) => {
        const reply = event.results[0][0].transcript.toLowerCase();

        console.log("Raw reply: ", reply);
        stopListening();

        const normalizedReply = reply
          .trim()
          .toLowerCase()
          .replace(/[\.,!?“”‘’"]/g, "") // remove punctuation and quotes
          .replace(/\s+/g, " "); // collapse multiple spaces

        const positiveKeywords = [
          // English (common confirmations)
          "yes",
          "yeah",
          "yep",
          "yup",
          "sure",
          "correct",
          "right",
          "ok",
          "okay",
          "of course",
          "absolutely",
          "definitely",
          "sure thing",

          // Hindi
          "हाँ",
          "हां",
          "हां जी",
          "जी",
          "जी हां",
          "बिलकुल",
          "बिल्कुल",
          "सही",
          "ठीक है",
          "हां बिल्कुल",
          "बिलकुल सही",
          "जी बिल्कुल",
          "हां ठीक है",

          // Marathi
          "हो",
          "होय",
          "हो ना",
          "हो अगदी",
          "हो नक्कीच",
          "हो खरं",
          "हो बरोबर",
          "नक्कीच",
          "बरोबर",
          "खरंय",
          "ठीक आहे",
          "हो खरच",
          "हो हो",
          "हो सरळ",
          "हो एकदम बरोबर",
          "येस",
          "यासा",
          "यासे",
          "येस रे",
          "येस हो",
          "येस न",
          "येसच",
          "यस",
          "येस्स",
        ];

        if (positiveKeywords.some((w) => normalizedReply.includes(w))) {
          handleSendMessage(transcriptToConfirm);
        } else {
          speak(
            "Okay, please say it again. " +
              questions[currentQIndex].question[selectedLang],
            () => startListening()
          );
        }
      };

      recognitionRef.current.start();
      isRecognizingRef.current = true;
      setListening(true);
    }
  };

  const startListening = () => {
    if (recognitionRef.current && !isRecognizingRef.current) {
      try {
        // Handle speech result
        recognitionRef.current.onresult = (event) => {
          resultReceivedRef.current = true;
          retryCountRef.current = 0;

          const transcript = event.results[0][0].transcript;
          setTranscript(transcript);
          stopListening();

          // Ask for user confirmation
          speak(`Did you say: "${transcript}"? Please say yes or no.`, () => {
            listenForConfirmation(transcript);
          });
        };

        // Handle recognition end
        recognitionRef.current.onend = () => {
          isRecognizingRef.current = false;
          setListening(false);

          if (!resultReceivedRef.current && !voluntaryStopRef.current) {
            if (retryCountRef.current < maxRetries) {
              retryCountRef.current++;
              console.log(`Retrying... attempt ${retryCountRef.current}`);
              speak("I didn't hear anything. Please speak again.", () => {
                setTimeout(() => startListening(), 500);
              });
            } else {
              retryCountRef.current = 0;
              speak("I couldn't hear anything after several attempts.", () => {
                stopListening();
              });
            }
          } else if (voluntaryStopRef.current) {
            retryCountRef.current = 0;
            voluntaryStopRef.current = false;
            console.log("Listening stopped by user.");
          }

          resultReceivedRef.current = false;
        };

        // Handle errors
        recognitionRef.current.onerror = (event) => {
          console.error("Speech Recognition Error:", event.error);
          isRecognizingRef.current = false;
          setListening(false);
        };

        // Start recognition
        recognitionRef.current.start();
        isRecognizingRef.current = true;
        setTranscript("");
        setReply("");
        setListening(true);
      } catch (error) {
        console.warn("Recognition start error:", error);
      }
    } else {
      console.log("Recognition already in progress");
    }
  };

  function validateBookingData(key, value) {
    switch (key) {
      case "name":
        // Minimum 2 characters, only letters and spaces allowed
        return (
          typeof value === "string" && /^[a-zA-Z\s]{2,}$/.test(value.trim())
        );

      case "phone":
        // Remove all non-digit characters
        const cleaned = value.replace(/\D/g, "");
        return /^[6-9]\d{9}$/.test(cleaned);

      case "email":
        // Simple email format validation
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

      case "checkin":
      case "checkout":
        // Format: DD/MM/YYYY, accepts leading zeros
        return /^(0?[1-9]|[12][0-9]|3[01])\/(0?[1-9]|1[0-2])\/(20\d{2})$/.test(
          value
        );

      case "people":
        // Must be a number between 1 and 20
        const numPeople = parseInt(value);
        return Number.isInteger(numPeople) && numPeople > 0 && numPeople <= 20;

      case "roomType":
        // Accept only "AC" or "Non-AC" (case-insensitive)
        return (
          typeof value === "string" &&
          ["AC", "NON-AC"].includes(value.toUpperCase())
        );

      case "roomCount":
        // Must be a number between 1 and 5
        const numRooms = parseInt(value);
        return Number.isInteger(numRooms) && numRooms > 0 && numRooms <= 5;

      case "upiRef":
        // Optional UPI reference validation — at least 6 alphanumeric chars
        return typeof value === "string"
          ? /^[a-zA-Z0-9]{6,}$/.test(value.trim())
          : true;

      default:
        // Unknown field — assume valid
        return true;
    }
  }

  return (
    <div style={styles.container}>
      <h2>
        🎤 Talk to <span style={{ color: "#10b981" }}>Evi</span>
      </h2>

      <div style={styles.dropdownContainer}>
        <label htmlFor="lang-select">🌐 Language: </label>
        <select
          id="lang-select"
          value={selectedLang}
          onChange={(e) => setSelectedLang(e.target.value)}
          style={styles.dropdown}
        >
          {Object.entries(languageOptions).map(([key, { label }]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <button
        onClick={() => {
          setStarted(true);
          setCurrentQIndex(0);
        }}
        disabled={listening}
        style={styles.button}
      >
        {listening ? "🎙️ Listening..." : "🟢 Speak to Evi"}
      </button>

      <button
        onClick={() => {
          setStarted(false);
          setCurrentQIndex(0);
          stopListening();
        }}
        disabled={!listening}
        style={{
          ...styles.stopButton,
          backgroundColor: listening ? "#dc2626" : "#f87171",
          cursor: listening ? "pointer" : "not-allowed",
        }}
      >
        {listening ? "🎙️ Stop Listening" : "🔴 Not Listening"}
      </button>

      {transcript && (
        <div style={styles.box}>
          <strong>🧑 You:</strong>
          <p>{transcript}</p>
        </div>
      )}

      {reply && (
        <div style={{ ...styles.box, backgroundColor: "#f0fff4" }}>
          <strong>🤖 Evi:</strong>
          <p>{reply}</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    width: "100%",
    maxWidth: "100%",
    padding: "16px",
    marginBottom: "20px",
    borderRadius: "12px",
    backgroundColor: "#f9fafb",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
    boxSizing: "border-box",
  },
  dropdownContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "12px",
  },
  dropdown: {
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    marginLeft: "8px",
    minWidth: "120px",
    fontSize: "14px",
  },

  stopButton: {
    color: "#fff",
    border: "none",
    margin: "10px",
    padding: "10px 20px",
    borderRadius: "8px",
    fontWeight: "bold",
    fontSize: "16px",
    marginTop: "12px",
  },

  button: {
    backgroundColor: "#10b981",
    color: "#fff",
    padding: "10px 16px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "15px",
    marginTop: "10px",
    width: "100%",
    maxWidth: "220px",
  },
  box: {
    marginTop: "16px",
    padding: "12px",
    backgroundColor: "#f3f4f6",
    borderRadius: "10px",
    fontSize: "14px",
    lineHeight: "1.6",
    wordBreak: "break-word",
  },
};

export default VoiceChat;
